"use client"

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import ReportService from '@/api/reports.service';
import ReportDataService from '@/api/reportData.service';
import {
  ArrowLeft,
  BarChart2,
  Building2,
  Calculator,
  CreditCard,
  DollarSign,
  FileText,
  Loader2,
  Calendar as CalendarIcon,
  Users,
  FilePlus2,
  FilePieChart,
  Save
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { IncomeStatementReport, BalanceSheetReport } from '@/components/reports';

export default function CreateReport() {
  const { reportType } = useParams<{ reportType: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [businessProperties, setBusinessProperties] = useState<any[]>([]);
  const [businessTenants, setBusinessTenants] = useState<any[]>([]);
  const [maintenanceCategories, setMaintenanceCategories] = useState<any[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [specificReportType, setSpecificReportType] = useState('');
  const [dateRange, setDateRange] = useState('monthly');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [showDateRange, setShowDateRange] = useState(false);
  const [properties, setProperties] = useState<string[]>([]);
  const [tenants, setTenants] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [reportData, setReportData] = useState<any>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);

  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;

  useEffect(() => {
    if (dateRange === 'custom') {
      setShowDateRange(true);
    } else {
      setShowDateRange(false);
    }
  }, [dateRange]);

  // Set default report type based on URL parameter
  useEffect(() => {
    if (reportType) {
      setDefaultReportTypeOptions(reportType);
    }
  }, [reportType]);

  const setDefaultReportTypeOptions = (type: string) => {
    if (type === 'financial') {
      setSpecificReportType('income_statement');
    } else if (type === 'property') {
      setSpecificReportType('rental_income');
      // Fetch properties
      fetchProperties();
    } else if (type === 'tenant') {
      setSpecificReportType('tenant_ledger');
      // Fetch tenants
      fetchTenants();
    } else if (type === 'maintenance') {
      setSpecificReportType('maintenance');
      // Fetch properties and maintenance categories
      fetchProperties();
      fetchMaintenanceCategories();
    } else if (type === 'loan') {
      setSpecificReportType('amortization');
    }
  };

  const fetchProperties = async () => {
    // This would fetch the properties from your API
    // For now, let's use placeholder data
    setBusinessProperties([
      { id: 'prop1', name: 'Property 1' },
      { id: 'prop2', name: 'Property 2' },
      { id: 'prop3', name: 'Property 3' },
    ]);
  };

  const fetchTenants = async () => {
    // This would fetch the tenants from your API
    // For now, let's use placeholder data
    setBusinessTenants([
      { id: 'tenant1', name: 'Tenant 1' },
      { id: 'tenant2', name: 'Tenant 2' },
      { id: 'tenant3', name: 'Tenant 3' },
    ]);
  };

  const fetchMaintenanceCategories = async () => {
    // This would fetch the maintenance categories from your API
    // For now, let's use placeholder data
    setMaintenanceCategories([
      { id: 'cat1', name: 'Repairs' },
      { id: 'cat2', name: 'Renovations' },
      { id: 'cat3', name: 'Regular Maintenance' },
    ]);
  };

  const validateForm = () => {
    if (!name) {
      toast({
        title: "Error",
        description: "Report name is required",
        variant: "destructive",
      });
      return false;
    }

    if (!specificReportType) {
      toast({
        title: "Error",
        description: "Report type is required",
        variant: "destructive",
      });
      return false;
    }

    if (dateRange === 'custom' && (!startDate || !endDate)) {
      toast({
        title: "Error",
        description: "Start and end dates are required for custom date range",
        variant: "destructive",
      });
      return false;
    }

    // Validate property selection for property reports
    if (reportType === 'property' && properties.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one property",
        variant: "destructive",
      });
      return false;
    }

    // Validate tenant selection for tenant reports
    if (reportType === 'tenant' && tenants.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one tenant",
        variant: "destructive",
      });
      return false;
    }

    // Validate loan details for loan reports
    if (reportType === 'loan') {
      if (!loanAmount || isNaN(Number(loanAmount))) {
        toast({
          title: "Error",
          description: "Please enter a valid loan amount",
          variant: "destructive",
        });
        return false;
      }

      if (!interestRate || isNaN(Number(interestRate))) {
        toast({
          title: "Error",
          description: "Please enter a valid interest rate",
          variant: "destructive",
        });
        return false;
      }

      if (!loanTerm || isNaN(Number(loanTerm))) {
        toast({
          title: "Error",
          description: "Please enter a valid loan term",
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  // Function to generate report data client-side
  const generateReport = async () => {
    if (!validateForm() || !businessId) return;
    
    setIsGeneratingReport(true);
    
    try {
      let reportResult = null;
      const startDateObj = dateRange === 'custom' ? startDate : new Date();
      const endDateObj = dateRange === 'custom' ? endDate : new Date();
      
      // Adjust dates based on dateRange if not custom
      if (dateRange !== 'custom') {
        const now = new Date();
        
        switch (dateRange) {
          case 'daily':
            startDateObj.setHours(0, 0, 0, 0);
            endDateObj.setHours(23, 59, 59, 999);
            break;
          case 'weekly':
            // Set to beginning of week (Sunday)
            startDateObj.setDate(now.getDate() - now.getDay());
            startDateObj.setHours(0, 0, 0, 0);
            // Set to end of week (Saturday)
            endDateObj.setDate(startDateObj.getDate() + 6);
            endDateObj.setHours(23, 59, 59, 999);
            break;
          case 'monthly':
            // Set to first day of month
            startDateObj.setDate(1);
            startDateObj.setHours(0, 0, 0, 0);
            // Set to last day of month
            endDateObj.setMonth(now.getMonth() + 1);
            endDateObj.setDate(0);
            endDateObj.setHours(23, 59, 59, 999);
            break;
          case 'quarterly':
            // Set to first day of quarter
            const quarter = Math.floor(now.getMonth() / 3);
            startDateObj.setMonth(quarter * 3);
            startDateObj.setDate(1);
            startDateObj.setHours(0, 0, 0, 0);
            // Set to last day of quarter
            endDateObj.setMonth(quarter * 3 + 3);
            endDateObj.setDate(0);
            endDateObj.setHours(23, 59, 59, 999);
            break;
          case 'annual':
            // Set to first day of year
            startDateObj.setMonth(0);
            startDateObj.setDate(1);
            startDateObj.setHours(0, 0, 0, 0);
            // Set to last day of year
            endDateObj.setMonth(11);
            endDateObj.setDate(31);
            endDateObj.setHours(23, 59, 59, 999);
            break;
        }
      }
      
      // Generate different report types
      switch (reportType) {
        case 'financial':
          switch (specificReportType) {
            case 'income_statement':
              reportResult = await ReportDataService.getIncomeStatementData(businessId, startDateObj, endDateObj);
              break;
            case 'balance_sheet':
              reportResult = await ReportDataService.getBalanceSheetData(businessId, endDateObj);
              break;
            case 'cash_flow':
              reportResult = await ReportDataService.getCashFlowData(businessId, startDateObj, endDateObj);
              break;
            // Add other financial report types as needed
          }
          break;
        case 'property':
          reportResult = await ReportDataService.getRentalIncomeData(businessId, properties, startDateObj, endDateObj);
          break;
        case 'tenant':
          reportResult = await ReportDataService.getTenantLedgerData(businessId, tenants, startDateObj, endDateObj);
          break;
        case 'maintenance':
          reportResult = await ReportDataService.getMaintenanceReportData(businessId, properties, categories, startDateObj, endDateObj);
          break;
        case 'loan':
          reportResult = ReportDataService.getAmortizationScheduleData(
            Number(loanAmount),
            Number(interestRate),
            Number(loanTerm),
            paymentFrequency
          );
          break;
      }
      
      setReportData(reportResult);
      setIsReportGenerated(true);
      
      toast({
        title: "Success",
        description: "Report generated successfully",
      });
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "An error occurred while generating the report",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Save report to server
  const handleSaveReport = async () => {
    if (!validateForm() || !businessId) return;
    
    setIsLoading(true);
    
    try {
      let reportData: any = {
        name,
        description,
        reportType: specificReportType,
        dateRange,
        status: 'draft',
      };

      if (dateRange === 'custom') {
        reportData.startDate = startDate;
        reportData.endDate = endDate;
      }

      let response;

      switch (reportType) {
        case 'financial':
          response = await ReportService.createFinancialReport(businessId, reportData);
          break;
        case 'property':
          reportData.propertyIds = properties;
          response = await ReportService.createPropertyReport(businessId, reportData);
          break;
        case 'tenant':
          reportData.tenantIds = tenants;
          response = await ReportService.createTenantReport(businessId, reportData);
          break;
        case 'maintenance':
          reportData.propertyIds = properties;
          reportData.categoryIds = categories;
          response = await ReportService.createMaintenanceReport(businessId, reportData);
          break;
        case 'loan':
          reportData.loanAmount = Number(loanAmount);
          reportData.interestRate = Number(interestRate);
          reportData.loanTerm = Number(loanTerm);
          reportData.paymentFrequency = paymentFrequency;
          response = await ReportService.createLoanReport(businessId, reportData);
          break;
        default:
          throw new Error("Invalid report type");
      }

      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Report saved successfully",
        });
        
        // Navigate to the reports list or to the new report
        if (response.data.report?.publicId) {
          navigate(`/report/${response.data.report.publicId}`);
        } else {
          navigate('/reports');
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save report",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving report:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the report",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the appropriate report component based on type
  const renderReportPreview = () => {
    if (!isReportGenerated || !reportData) return null;
    
    switch (reportType) {
      case 'financial':
        switch (specificReportType) {
          case 'income_statement':
            return (
              <div className="mt-6">
                <h2 className="text-xl font-semibold mb-4">Report Preview</h2>
                <IncomeStatementReport 
                  reportData={reportData}
                  name={name}
                  onSave={handleSaveReport}
                />
              </div>
            );
          case 'balance_sheet':
            return (
              <div className="mt-6">
                <h2 className="text-xl font-semibold mb-4">Report Preview</h2>
                <BalanceSheetReport 
                  reportData={reportData}
                  name={name}
                  onSave={handleSaveReport}
                />
              </div>
            );
          // Add other financial report types as needed
        }
        break;
      // Add other report type previews as needed
    }
    
    return (
      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-4">Report Preview</h2>
        <Card>
          <CardHeader>
            <CardTitle>Report Generated</CardTitle>
            <CardDescription>Your report has been generated. You can export it or save it to your reports.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleSaveReport}>
                <Save className="w-4 h-4 mr-2" />
                Save Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const getReportTypeOptionsBasedOnCategory = () => {
    switch (reportType) {
      case 'financial':
        return (
          <>
            <SelectItem value="income_statement">Income Statement</SelectItem>
            <SelectItem value="balance_sheet">Balance Sheet</SelectItem>
            <SelectItem value="cash_flow">Cash Flow Statement</SelectItem>
            <SelectItem value="changes_in_equity">Statement of Changes in Equity</SelectItem>
          </>
        );
      case 'property':
        return (
          <>
            <SelectItem value="rental_income">Rental Income Report</SelectItem>
            <SelectItem value="occupancy">Occupancy Report</SelectItem>
            <SelectItem value="vacancy">Vacancy Analysis</SelectItem>
          </>
        );
      case 'tenant':
        return (
          <>
            <SelectItem value="tenant_ledger">Tenant Ledger</SelectItem>
            <SelectItem value="aging_debtors">Aging of Rental Debtors</SelectItem>
          </>
        );
      case 'maintenance':
        return (
          <>
            <SelectItem value="maintenance">Maintenance/Repairs Report</SelectItem>
            <SelectItem value="capex">Capital Expenditure Report</SelectItem>
          </>
        );
      case 'loan':
        return (
          <>
            <SelectItem value="amortization">Loan/Mortgage Amortization Schedule</SelectItem>
          </>
        );
      default:
        return null;
    }
  };

  const toggleProperty = (propertyId: string, checked: boolean) => {
    if (checked) {
      setProperties(prev => [...prev, propertyId]);
    } else {
      setProperties(prev => prev.filter(id => id !== propertyId));
    }
  };

  const toggleTenant = (tenantId: string, checked: boolean) => {
    if (checked) {
      setTenants(prev => [...prev, tenantId]);
    } else {
      setTenants(prev => prev.filter(id => id !== tenantId));
    }
  };

  const toggleCategory = (categoryId: string, checked: boolean) => {
    if (checked) {
      setCategories(prev => [...prev, categoryId]);
    } else {
      setCategories(prev => prev.filter(id => id !== categoryId));
    }
  };

  const renderExtraFields = () => {
    switch (reportType) {
      case 'property':
        return (
          <div className="space-y-4">
            <Label>Select Properties</Label>
            <div className="border rounded-md p-4 space-y-2 max-h-60 overflow-y-auto">
              {businessProperties.map(property => (
                <div key={property.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`property-${property.id}`} 
                    onCheckedChange={(checked) => toggleProperty(property.id, !!checked)}
                  />
                  <Label htmlFor={`property-${property.id}`}>{property.name}</Label>
                </div>
              ))}
            </div>
          </div>
        );
      case 'tenant':
        return (
          <div className="space-y-4">
            <Label>Select Tenants</Label>
            <div className="border rounded-md p-4 space-y-2 max-h-60 overflow-y-auto">
              {businessTenants.map(tenant => (
                <div key={tenant.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`tenant-${tenant.id}`} 
                    onCheckedChange={(checked) => toggleTenant(tenant.id, !!checked)}
                  />
                  <Label htmlFor={`tenant-${tenant.id}`}>{tenant.name}</Label>
                </div>
              ))}
            </div>
          </div>
        );
      case 'maintenance':
        return (
          <div className="space-y-4">
            <div>
              <Label>Select Properties</Label>
              <div className="border rounded-md p-4 space-y-2 max-h-40 overflow-y-auto">
                {businessProperties.map(property => (
                  <div key={property.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`property-${property.id}`} 
                      onCheckedChange={(checked) => toggleProperty(property.id, !!checked)}
                    />
                    <Label htmlFor={`property-${property.id}`}>{property.name}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Select Categories</Label>
              <div className="border rounded-md p-4 space-y-2 max-h-40 overflow-y-auto">
                {maintenanceCategories.map(category => (
                  <div key={category.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`category-${category.id}`} 
                      onCheckedChange={(checked) => toggleCategory(category.id, !!checked)}
                    />
                    <Label htmlFor={`category-${category.id}`}>{category.name}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'loan':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="loan-amount">Loan Amount</Label>
              <Input
                id="loan-amount"
                type="number"
                placeholder="e.g. 250000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="interest-rate">Interest Rate (%)</Label>
              <Input
                id="interest-rate"
                type="number"
                placeholder="e.g. 4.5"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="loan-term">Loan Term (months)</Label>
              <Input
                id="loan-term"
                type="number"
                placeholder="e.g. 360"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="payment-frequency">Payment Frequency</Label>
              <Select value={paymentFrequency} onValueChange={setPaymentFrequency}>
                <SelectTrigger id="payment-frequency">
                  <SelectValue placeholder="Select payment frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getReportTypeIcon = () => {
    switch (reportType) {
      case 'financial':
        return <BarChart2 className="w-5 h-5 text-primary mr-2" />;
      case 'property':
        return <Building2 className="w-5 h-5 text-primary mr-2" />;
      case 'tenant':
        return <Users className="w-5 h-5 text-primary mr-2" />;
      case 'maintenance':
        return <FilePlus2 className="w-5 h-5 text-primary mr-2" />;
      case 'loan':
        return <CreditCard className="w-5 h-5 text-primary mr-2" />;
      default:
        return <FileText className="w-5 h-5 text-primary mr-2" />;
    }
  };

  const getReportTypeName = () => {
    switch (reportType) {
      case 'financial':
        return 'Financial Report';
      case 'property':
        return 'Property Report';
      case 'tenant':
        return 'Tenant Report';
      case 'maintenance':
        return 'Maintenance Report';
      case 'loan':
        return 'Loan Report';
      default:
        return 'Report';
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Create {getReportTypeName()}</h1>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center">
              {getReportTypeIcon()}
              <CardTitle>New {getReportTypeName()}</CardTitle>
            </div>
            <CardDescription>
              Fill in the details to create your report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => { e.preventDefault(); generateReport(); }} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="report-name">Report Name</Label>
                  <Input
                    id="report-name"
                    placeholder="Enter a name for this report"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="report-description">Description (Optional)</Label>
                  <Textarea
                    id="report-description"
                    placeholder="Enter a description for this report"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="resize-none"
                  />
                </div>

                <div>
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select value={specificReportType} onValueChange={setSpecificReportType}>
                    <SelectTrigger id="report-type">
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      {getReportTypeOptionsBasedOnCategory()}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="date-range">Date Range</Label>
                  <Select value={dateRange} onValueChange={setDateRange}>
                    <SelectTrigger id="date-range">
                      <SelectValue placeholder="Select date range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="custom">Custom Date Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {showDateRange && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start-date">Start Date</Label>
                      <div className="relative">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !startDate && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {startDate ? format(startDate, "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={startDate}
                              onSelect={(date) => date && setStartDate(date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="end-date">End Date</Label>
                      <div className="relative">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !endDate && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {endDate ? format(endDate, "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={endDate}
                              onSelect={(date) => date && setEndDate(date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                )}

                {/* Extra fields based on report type */}
                {renderExtraFields()}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/reports')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isGeneratingReport}>
                  {isGeneratingReport ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Report Preview */}
        {renderReportPreview()}
      </div>
    </div>
  );
} 